import { memo, useState, useMemo } from 'react';
import { Handle, type NodeProps, type Node } from '@xyflow/react';
import { makeStyles, tokens, Spinner } from '@fluentui/react-components';
import { FlashFilled } from '@fluentui/react-icons';
import { useConnectorInfo, useConnectorContext } from '@/contexts';
import { actionNameKey } from '@/hooks/useWorkflowConnectors';
import { useHandleLayout } from './useHandlePositions';
import { useNodeConnectionStatus } from '@/hooks/useNodeConnectionStatus';
import { useNodeValidationErrors } from '@/hooks/useProactiveValidation';
import { ExecutionOverlay } from '@/components/shared';
import { ValidationErrorBadge } from './ValidationErrorBadge';
import {
  FloatingNode,
  NodeHoverToolbar,
  useDetailLevel,
  type NodeStatus,
} from './FloatingNode';
import { getBuiltInManifest } from '@/data/manifests';
import type { NodeExecutionStatus } from '@/types/monitoring';

export type FloatingTriggerNodeData = {
  label: string;
  actionType: string;
  actionName: string;
  kind?: string;
  connectionName?: string;
  // Monitoring mode props
  executionStatus?: NodeExecutionStatus;
  executionDuration?: number;
  // Toolbar action callbacks (injected by WorkflowDesigner)
  onOpen?: () => void;
  onDelete?: () => void;
  onEditWithAI?: () => void;
};

export type FloatingTriggerNodeType = Node<FloatingTriggerNodeData, 'floatingTrigger'>;

const useStyles = makeStyles({
  wrapper: {
    position: 'relative',
  },
  connectorIcon: {
    width: '20px',
    height: '20px',
    borderRadius: tokens.borderRadiusSmall,
  },
  handleOutput: {
    width: '8px',
    height: '8px',
    backgroundColor: tokens.colorNeutralStrokeAccessible,
    transitionProperty: 'width, height, background-color',
    transitionDuration: '0.15s',
    transitionTimingFunction: 'ease-out',
    '::after': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '40px',
      height: '40px',
    },
    ':hover': {
      width: '10px',
      height: '10px',
      backgroundColor: tokens.colorBrandBackground,
    },
  },
});

/**
 * Floating-style Trigger Node with minimal visual footprint.
 * Shows icon + label by default, toolbar on hover.
 */
export const FloatingTriggerNode = memo(
  ({ data, selected, id }: NodeProps<FloatingTriggerNodeType>) => {
    const styles = useStyles();
    const { isLoading } = useConnectorContext();
    // See FloatingActionNode for the connection-vs-action lookup rationale.
    const connectorInfoByConnection = useConnectorInfo(data.connectionName);
    const connectorInfoByAction = useConnectorInfo(actionNameKey(data.actionName));
    const connectorInfo = connectorInfoByConnection ?? connectorInfoByAction;
    const [imageError, setImageError] = useState(false);
    const handles = useHandleLayout();
    const detailLevel = useDetailLevel();

    // Get connection validation status
    // Pass connectionReferenceKey from workflow data to validate against actual connection
    const { validationResult } = useNodeConnectionStatus({
      nodeId: id,
      operationType: data.actionType,
      connectionReferenceKey: data.connectionName,
    });

    // Get proactive validation errors for this node
    const { errors: validationErrors } = useNodeValidationErrors(data.actionName);

    // Show loading state when we have a connection but no info yet
    const showLoading = isLoading && data.connectionName && !connectorInfo;

    // Get built-in manifest info as fallback for triggers without connections
    const builtInManifest = useMemo(() => {
      if (connectorInfo) return null;
      return getBuiltInManifest(data.actionType, data.kind);
    }, [connectorInfo, data.actionType, data.kind]);

    // Use connector info for display, falling back to the built-in manifest
    // (e.g. for placeholder triggers), then to the action type.
    const displayName = connectorInfo?.displayName ?? data.actionType;
    const iconUri = connectorInfo?.iconUri ?? (builtInManifest?.properties?.iconUri as string | undefined);
    const brandColor = connectorInfo?.brandColor ?? (builtInManifest?.properties?.brandColor as string | undefined);
    const showIcon = iconUri && !imageError;

    // Map execution status to FloatingNode status
    const getNodeStatus = (): NodeStatus => {
      if (!data.executionStatus) {
        // Check validation status when not in monitoring mode
        if (validationResult) {
          if (validationResult.status === 'mock-active') return 'mock';
          if (!validationResult.isValid) {
            return validationResult.status === 'warning' ? 'warning' : 'error';
          }
        }
        return 'default';
      }

      switch (data.executionStatus) {
        case 'running':
          return 'running';
        case 'succeeded':
          return 'success';
        case 'failed':
          return 'failed';
        case 'skipped':
          return 'skipped';
        case 'pending':
          return 'default';
        default:
          return 'default';
      }
    };

    // Build status message for error/mock badge tooltip
    const statusMessage =
      validationResult?.status === 'mock-active'
        ? 'Using test data'
        : validationResult && !validationResult.isValid
          ? validationResult.error?.message ?? 'Connection not configured'
          : undefined;

    // Render the icon
    const renderIcon = () => {
      if (showLoading) {
        return <Spinner size="extra-tiny" aria-label="Loading connector info" />;
      }

      if (showIcon) {
        return (
          <img
            src={iconUri}
            alt=""
            className={styles.connectorIcon}
            aria-hidden="true"
            onError={() => setImageError(true)}
          />
        );
      }

      return <FlashFilled aria-hidden="true" />;
    };

    const isMonitoringMode = data.executionStatus !== undefined;

    return (
      <div className={styles.wrapper}>
        <NodeHoverToolbar
          onConfigure={data.onOpen}
          onDelete={data.onDelete}
          onEditWithAI={data.onEditWithAI}
          showToolbar={!isMonitoringMode}
          disabled={selected}
        >
          <FloatingNode
            icon={renderIcon()}
            label={data.label}
            subLabel={showLoading ? 'Loading...' : displayName}
            detailLevel={detailLevel}
            selected={selected}
            status={getNodeStatus()}
            statusMessage={statusMessage}
            accentColor="green"
            brandColorOverride={brandColor}
            shape="circle"
            ariaLabel={`${data.label} ${displayName} trigger node`}
          >
            {/* Triggers only have output handles */}
            <Handle
              type="source"
              position={handles.output.position}
              id="output"
              isConnectable={true}
              className={styles.handleOutput}
              style={handles.output.style}
            />
          </FloatingNode>
        </NodeHoverToolbar>

        {/* Show execution overlay in monitoring mode */}
        {isMonitoringMode && (
          <ExecutionOverlay
            status={data.executionStatus!}
            duration={data.executionDuration}
            position="top-right"
          />
        )}

        {/* Show validation error badge (only in design mode) */}
        {!isMonitoringMode && validationErrors.length > 0 && (
          <ValidationErrorBadge
            nodeName={data.actionName}
            errors={validationErrors}
            position="top-left"
            size="small"
          />
        )}
      </div>
    );
  }
);

FloatingTriggerNode.displayName = 'FloatingTriggerNode';
